// notificationService.js v1.1.0 - Notification & Alert Service for LYT Communications
// Handles: expiring cert/COI alerts, push notifications, email triggers via Gateway, daily compliance digest

const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbwiq8NzgdUQ6Hu44NHN3ASdAYTd68uK6wGRK_CpJroSoiMuv66aRmPAzDxmtXexl6MK/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';

// Notification types
export const NOTIFICATION_TYPES = {
  CERT_EXPIRING: 'cert_expiring',
  COI_EXPIRING: 'coi_expiring',
  LICENSE_EXPIRING: 'license_expiring',
  VEHICLE_INSURANCE_EXPIRING: 'vehicle_insurance_expiring',
  QC_APPROVAL_NEEDED: 'qc_approval_needed',
  ASSIGNMENT_NEW: 'assignment_new',
  ISSUE_REPORTED: 'issue_reported',
  ISSUE_RESOLVED: 'issue_resolved',
  STATUS_CHANGE: 'status_change',
  PHOTO_MISSING: 'photo_missing',
  POTHOLE_SUBMITTED: 'pothole_submitted',
  POTHOLE_APPROVED: 'pothole_approved',
  SEGMENT_COMPLETE: 'segment_complete',
  LOCATE_EXPIRING: 'locate_expiring',
  LOCATE_EXPIRED: 'locate_expired',
  INVOICE_SUBMITTED: 'invoice_submitted',
  INVOICE_APPROVED: 'invoice_approved',
  INVOICE_REJECTED: 'invoice_rejected',
  PAYMENT_RECEIVED: 'payment_received',
  CHANGE_ORDER_SUBMITTED: 'change_order_submitted',
  CHANGE_ORDER_APPROVED: 'change_order_approved',
  ASSIGNMENT_ACCEPTED: 'assignment_accepted',
  ASSIGNMENT_DECLINED: 'assignment_declined',
};

// Severity levels
export const SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  URGENT: 'urgent',
  CRITICAL: 'critical',
};

// ========== EXPIRATION CHECKING ==========

/**
 * Check all compliance items for upcoming expirations
 * Returns array of notification objects
 */
export async function checkExpirations(userEmail, userRole) {
  const notifications = [];
  const now = new Date();
  const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const sevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  try {
    // Fetch user's compliance data from onboarding sheet
    const response = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsRead',
        sheetId: '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc',
        range: userRole === 'contractor' ? 'Contractor Submissions!A:Z' : 'Employee Submissions!A:Z',
      }),
    });

    if (!response.ok) return notifications;
    const data = await response.json();
    if (!data.data || data.data.length < 2) return notifications;

    const headers = data.data[0];
    const rows = data.data.slice(1);

    // Find rows matching this user
    const emailIdx = headers.findIndex(h => h && h.toString().toLowerCase().includes('email'));
    const userRows = rows.filter(r => r[emailIdx] && r[emailIdx].toString().toLowerCase() === userEmail.toLowerCase());

    if (userRows.length === 0) return notifications;
    const userRow = userRows[userRows.length - 1]; // Most recent submission

    // Check expiration fields
    const expirationFields = [
      { headerMatch: 'coi_expiration', type: NOTIFICATION_TYPES.COI_EXPIRING, label: 'Certificate of Insurance' },
      { headerMatch: 'license_expiration', type: NOTIFICATION_TYPES.LICENSE_EXPIRING, label: 'Business License' },
      { headerMatch: 'vehicle_insurance_exp', type: NOTIFICATION_TYPES.VEHICLE_INSURANCE_EXPIRING, label: 'Vehicle Insurance' },
      { headerMatch: 'cert_expiration', type: NOTIFICATION_TYPES.CERT_EXPIRING, label: 'Certification' },
      { headerMatch: 'osha_expiration', type: NOTIFICATION_TYPES.CERT_EXPIRING, label: 'OSHA Certification' },
      { headerMatch: 'flagger_cert_exp', type: NOTIFICATION_TYPES.CERT_EXPIRING, label: 'Flagger Certification' },
    ];

    for (const field of expirationFields) {
      const idx = headers.findIndex(h => h && h.toString().toLowerCase().includes(field.headerMatch));
      if (idx === -1 || !userRow[idx]) continue;

      const expDate = new Date(userRow[idx]);
      if (isNaN(expDate.getTime())) continue;

      if (expDate < now) {
        notifications.push({
          type: field.type,
          severity: SEVERITY.CRITICAL,
          title: `${field.label} EXPIRED`,
          message: `Your ${field.label} expired on ${expDate.toLocaleDateString()}. Please update immediately.`,
          expirationDate: expDate.toISOString(),
          daysUntilExpiry: Math.floor((expDate - now) / (1000 * 60 * 60 * 24)),
          createdAt: now.toISOString(),
        });
      } else if (expDate < sevenDays) {
        notifications.push({
          type: field.type,
          severity: SEVERITY.URGENT,
          title: `${field.label} expiring in ${Math.ceil((expDate - now) / (1000 * 60 * 60 * 24))} days`,
          message: `Your ${field.label} expires on ${expDate.toLocaleDateString()}. Please renew soon.`,
          expirationDate: expDate.toISOString(),
          daysUntilExpiry: Math.ceil((expDate - now) / (1000 * 60 * 60 * 24)),
          createdAt: now.toISOString(),
        });
      } else if (expDate < thirtyDays) {
        notifications.push({
          type: field.type,
          severity: SEVERITY.WARNING,
          title: `${field.label} expiring in ${Math.ceil((expDate - now) / (1000 * 60 * 60 * 24))} days`,
          message: `Your ${field.label} expires on ${expDate.toLocaleDateString()}.`,
          expirationDate: expDate.toISOString(),
          daysUntilExpiry: Math.ceil((expDate - now) / (1000 * 60 * 60 * 24)),
          createdAt: now.toISOString(),
        });
      }
    }
  } catch (err) {
    console.error('checkExpirations error:', err);
  }

  return notifications;
}

// ========== EMAIL NOTIFICATIONS ==========

/**
 * Send an email notification via Gateway
 */
export async function sendEmailNotification(toEmail, subject, htmlBody) {
  try {
    const response = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'gmailSend',
        to: toEmail,
        subject: `[LYT Communications] ${subject}`,
        htmlBody: wrapEmailTemplate(subject, htmlBody),
      }),
    });
    const data = await response.json();
    return data.success || false;
  } catch (err) {
    console.error('sendEmailNotification error:', err);
    return false;
  }
}

/**
 * Send expiration alert emails to admins
 */
export async function sendExpirationAlertToAdmins(notifications, userName, userEmail) {
  if (!notifications || notifications.length === 0) return;

  const adminEmails = ['matt@lytcomm.com', 'mason@lytcomm.com', 'donnie@lytcomm.com'];
  const criticalItems = notifications.filter(n => n.severity === SEVERITY.CRITICAL || n.severity === SEVERITY.URGENT);

  if (criticalItems.length === 0) return;

  const itemsList = criticalItems.map(n =>
    `<tr><td style="padding:8px;border-bottom:1px solid #eee;">${n.title}</td><td style="padding:8px;border-bottom:1px solid #eee;color:${n.severity === 'critical' ? '#dc3545' : '#ffc107'}">${n.severity.toUpperCase()}</td><td style="padding:8px;border-bottom:1px solid #eee;">${n.daysUntilExpiry < 0 ? 'EXPIRED' : n.daysUntilExpiry + ' days'}</td></tr>`
  ).join('');

  const body = `
    <p><strong>${userName}</strong> (${userEmail}) has compliance items requiring attention:</p>
    <table style="width:100%;border-collapse:collapse;margin:16px 0;">
      <tr style="background:#f8f9fa;"><th style="padding:8px;text-align:left;">Item</th><th style="padding:8px;text-align:left;">Severity</th><th style="padding:8px;text-align:left;">Time Remaining</th></tr>
      ${itemsList}
    </table>
    <p>Please follow up with this team member to ensure compliance is maintained.</p>
  `;

  for (const admin of adminEmails) {
    await sendEmailNotification(admin, `Compliance Alert: ${userName}`, body);
  }
}

// ========== PROJECT MAP NOTIFICATIONS ==========

/**
 * Notify admins when QC approval is needed
 */
export async function notifyQCNeeded(segmentId, workType, submittedBy) {
  try {
    const subject = `QC Approval Needed: ${segmentId} (${workType})`;
    const body = `
      <p><strong>${submittedBy}</strong> has marked <strong>${workType}</strong> as complete for segment <strong>${segmentId}</strong>.</p>
      <p>Please review and approve/reject in the <a href="https://lytcomm.com/#project-map">Project Map</a>.</p>
    `;

    for (const admin of ADMIN_EMAILS) {
      await sendEmailNotification(admin, subject, body);
    }
    addNotification({ type: NOTIFICATION_TYPES.QC_APPROVAL_NEEDED, severity: SEVERITY.INFO,
      title: `QC Needed: ${segmentId}`, message: `${submittedBy} completed ${workType}` });
  } catch (err) {
    console.warn('[notificationService] notifyQCNeeded failed:', err);
  }
}

/**
 * Notify contractor of new assignment
 */
export async function notifyNewAssignment(contractorEmail, projectName, segmentIds, workType) {
  const subject = `New Assignment: ${projectName}`;
  const segList = Array.isArray(segmentIds) ? segmentIds : [];
  const preview = segList.slice(0, 10).join(', ');
  const overflow = segList.length > 10 ? ` +${segList.length - 10} more` : '';
  const body = `
    <h2 style="color:#0077B6; margin:0 0 16px">New Work Assignment</h2>
    <p>You've been assigned <strong>${segList.length} ${workType}</strong> segment${segList.length !== 1 ? 's' : ''} on project <strong>${projectName}</strong>.</p>
    ${preview ? `<p style="font-size:13px;color:#666;"><strong>Segments:</strong> ${preview}${overflow}</p>` : ''}
    <p style="margin:20px 0">
      <a href="https://lytcomm.com/#contractor-dashboard"
         style="background:#00b4d8; color:#fff; padding:12px 24px; border-radius:8px; text-decoration:none; font-weight:600; display:inline-block">
        Review &amp; Accept
      </a>
    </p>
    <p style="font-size:12px;color:#999;">Log in to your contractor dashboard to accept or decline this assignment.</p>
  `;

  await sendEmailNotification(contractorEmail, subject, body);
}

/**
 * Notify contractor when work is approved or rejected
 */
export async function notifyQCResult(contractorEmail, segmentId, workType, approved, notes) {
  try {
    const subject = `QC ${approved ? 'Approved' : 'Rejected'}: ${segmentId} (${workType})`;
    const body = `
      <p>Your <strong>${workType}</strong> work on segment <strong>${segmentId}</strong> has been <strong style="color:${approved ? '#28a745' : '#dc3545'}">${approved ? 'APPROVED' : 'REJECTED'}</strong>.</p>
      ${notes ? `<p><strong>Notes:</strong> ${notes}</p>` : ''}
      ${!approved ? '<p>Please review the feedback and address any issues in the <a href="https://lytcomm.com/#project-map">Project Map</a>.</p>' : ''}
    `;

    await sendEmailNotification(contractorEmail, subject, body);
  } catch (err) {
    console.warn('[notificationService] notifyQCResult failed:', err);
  }
}

// ========== PAYMENT & BILLING NOTIFICATIONS ==========

/**
 * Notify admins when a daily production report is submitted to client
 */
export async function notifyProductionSubmitted(submissionDate, segmentCount, totalAmount, submittedBy) {
  const subject = `Production Report Submitted: ${submissionDate}`;
  const body = `
    <p><strong>${submittedBy}</strong> submitted a daily production report.</p>
    <table style="width:100%;border-collapse:collapse;margin:12px 0;">
      <tr><td style="padding:6px 12px;border:1px solid #eee;"><strong>Date</strong></td><td style="padding:6px 12px;border:1px solid #eee;">${submissionDate}</td></tr>
      <tr><td style="padding:6px 12px;border:1px solid #eee;"><strong>Segments</strong></td><td style="padding:6px 12px;border:1px solid #eee;">${segmentCount}</td></tr>
      <tr><td style="padding:6px 12px;border:1px solid #eee;"><strong>Total</strong></td><td style="padding:6px 12px;border:1px solid #eee;">$${parseFloat(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
    </table>
    <p>View details in the <a href="https://lytcomm.com/#payment-tracker">Payment Tracker</a>.</p>
  `;
  for (const admin of ADMIN_EMAILS) {
    await sendEmailNotification(admin, subject, body);
  }
}

/**
 * Notify admins when client approval is logged
 */
export async function notifyClientApproval(type, referenceId, amount) {
  const label = type === 'production' ? 'Production Report' : 'Invoice';
  const subject = `Client Approved: ${label} ${referenceId}`;
  const body = `
    <p><strong>${label} ${referenceId}</strong> has been approved by the client.</p>
    <p><strong>Amount:</strong> $${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
    <p>Payment should arrive within 3-7 business days.</p>
    <p>Track in the <a href="https://lytcomm.com/#payment-tracker">Payment Tracker</a>.</p>
  `;
  for (const admin of ADMIN_EMAILS) {
    await sendEmailNotification(admin, subject, body);
  }
}

/**
 * Notify admins when payment is received
 */
export async function notifyPaymentReceived(type, referenceId, amount, paymentReference) {
  const label = type === 'production' ? 'Production Report' : 'Invoice';
  const subject = `Payment Received: ${label} ${referenceId} — $${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  const body = `
    <p>Payment received for <strong>${label} ${referenceId}</strong>.</p>
    <table style="width:100%;border-collapse:collapse;margin:12px 0;">
      <tr><td style="padding:6px 12px;border:1px solid #eee;"><strong>Amount</strong></td><td style="padding:6px 12px;border:1px solid #eee;">$${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
      <tr><td style="padding:6px 12px;border:1px solid #eee;"><strong>Reference</strong></td><td style="padding:6px 12px;border:1px solid #eee;">${paymentReference || 'N/A'}</td></tr>
    </table>
    <p>View all payments in the <a href="https://lytcomm.com/#payment-tracker">Payment Tracker</a>.</p>
  `;
  for (const admin of ADMIN_EMAILS) {
    await sendEmailNotification(admin, subject, body);
  }
}

/**
 * Notify admins when a contractor submits an invoice for review (WORKFLOW Step 128)
 */
export async function notifyContractorInvoiceSubmitted(invoiceNumber, contractorCompany, contractorEmail, netAmount) {
  try {
    const subject = `Contractor Invoice Submitted: ${invoiceNumber}`;
    const body = `
      <p><strong>${contractorCompany}</strong> (${contractorEmail}) submitted invoice <strong>${invoiceNumber}</strong>.</p>
      <p><strong>Net Amount:</strong> $${parseFloat(netAmount || 0).toFixed(2)}</p>
      <p>Review in the <a href="https://lytcomm.com/#payment-tracker">Payment Tracker</a>.</p>
    `;
    for (const admin of ADMIN_EMAILS) {
      await sendEmailNotification(admin, subject, body);
    }
    addNotification({ type: NOTIFICATION_TYPES.INVOICE_SUBMITTED, severity: SEVERITY.INFO,
      title: `Invoice: ${invoiceNumber}`, message: `${contractorCompany} — $${parseFloat(netAmount || 0).toFixed(2)}` });
  } catch (err) {
    console.warn('[notificationService] notifyContractorInvoiceSubmitted failed:', err);
  }
}

/**
 * Notify contractor when their invoice is approved or rejected (WORKFLOW Steps 127/135)
 */
export async function notifyContractorInvoiceResult(contractorEmail, invoiceNumber, approved, reason) {
  try {
    const status = approved ? 'APPROVED' : 'REJECTED';
    const color = approved ? '#28a745' : '#dc3545';
    const subject = `Invoice ${approved ? 'Approved' : 'Rejected'}: ${invoiceNumber}`;
    const body = `
      <p>Your invoice <strong>${invoiceNumber}</strong> has been <strong style="color:${color}">${status}</strong>.</p>
      ${reason ? `<p><strong>Notes:</strong> ${reason}</p>` : ''}
      ${!approved ? '<p>Please review and resubmit via your <a href="https://lytcomm.com/#contractor-dashboard">Dashboard</a>.</p>' : ''}
    `;
    await sendEmailNotification(contractorEmail, subject, body);
  } catch (err) {
    console.warn('[notificationService] notifyContractorInvoiceResult failed:', err);
  }
}

/**
 * Notify admins when a traditional invoice is submitted to client
 */
export async function notifyInvoiceSubmitted(invoiceNumber, invoiceType, netAmount, submittedBy) {
  const subject = `Invoice Submitted: ${invoiceNumber} (${invoiceType})`;
  const body = `
    <p><strong>${submittedBy}</strong> submitted invoice <strong>${invoiceNumber}</strong>.</p>
    <table style="width:100%;border-collapse:collapse;margin:12px 0;">
      <tr><td style="padding:6px 12px;border:1px solid #eee;"><strong>Type</strong></td><td style="padding:6px 12px;border:1px solid #eee;">${invoiceType}</td></tr>
      <tr><td style="padding:6px 12px;border:1px solid #eee;"><strong>Net Amount</strong></td><td style="padding:6px 12px;border:1px solid #eee;">$${parseFloat(netAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
    </table>
    <p>Track in the <a href="https://lytcomm.com/#payment-tracker">Payment Tracker</a>.</p>
  `;
  for (const admin of ADMIN_EMAILS) {
    await sendEmailNotification(admin, subject, body);
  }
}

/**
 * Notify admin when a worker submits onboarding with a proposed rate needing approval
 */
export async function notifyWorkerRateApproval(workerName, workerEmail, workerPhone, rateType, rateAmount, project) {
  try {
    const rateLabel = rateType === 'hourly' ? 'Hourly' : 'Day Rate';
    const rateUnit = rateType === 'hourly' ? 'HR' : 'DAY';
    const subject = `New Worker Rate Approval Needed: ${workerName}`;
    const body = `
      <p>A new worker has submitted onboarding and needs rate approval.</p>
      <table style="width:100%;border-collapse:collapse;margin:12px 0;">
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;width:140px;">Name</td><td style="padding:8px 12px;border:1px solid #eee;">${workerName}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Email</td><td style="padding:8px 12px;border:1px solid #eee;"><a href="mailto:${workerEmail}">${workerEmail}</a></td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Phone</td><td style="padding:8px 12px;border:1px solid #eee;">${workerPhone}</td></tr>
      </table>
      <div style="background:#f0f9ff;border-left:4px solid #0077B6;padding:16px;margin:16px 0;border-radius:0 8px 8px 0;">
        <h3 style="margin:0 0 8px;color:#0077B6;">Proposed Rate</h3>
        <p style="margin:0;font-size:1.4em;font-weight:700;color:#1f2937;">$${parseFloat(rateAmount).toFixed(2)} / ${rateUnit}</p>
        <p style="margin:4px 0 0;color:#6b7280;">${rateLabel} &bull; ${project}</p>
      </div>
      <p><strong>Action needed:</strong> Reply to this email or contact the worker directly to approve or adjust this rate.</p>
      <p>View all submissions in the <a href="https://lytcomm.com/#admin-dashboard">Admin Dashboard</a>.</p>
    `;
    await sendEmailNotification('matt@lytcomm.com', subject, body);
  } catch (err) {
    console.warn('[notificationService] notifyWorkerRateApproval failed:', err);
  }
}

/**
 * Send SMS notification via Twilio API endpoint
 */
export async function sendSMS(phoneNumber, message) {
  try {
    const response = await fetch('/api/send-sms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: phoneNumber, message }),
    });
    const data = await response.json();
    return data.success || false;
  } catch (err) {
    console.error('sendSMS error:', err);
    return false;
  }
}

// ========== IN-APP NOTIFICATION STORAGE ==========

const NOTIFICATION_STORAGE_KEY = 'lyt_notifications';

/**
 * Get stored notifications for current user
 */
export function getStoredNotifications() {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
}

/**
 * Add a notification to local storage
 */
export function addNotification(notification) {
  const notifications = getStoredNotifications();
  const newNotif = {
    id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    read: false,
    ...notification,
    createdAt: notification.createdAt || new Date().toISOString(),
  };
  notifications.unshift(newNotif);
  // Keep max 100 notifications
  if (notifications.length > 100) notifications.length = 100;
  localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(notifications));
  return newNotif;
}

/**
 * Mark notification as read
 */
export function markAsRead(notificationId) {
  const notifications = getStoredNotifications();
  const notif = notifications.find(n => n.id === notificationId);
  if (notif) {
    notif.read = true;
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(notifications));
  }
}

/**
 * Mark all as read
 */
export function markAllAsRead() {
  const notifications = getStoredNotifications();
  notifications.forEach(n => { n.read = true; });
  localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(notifications));
}

/**
 * Get unread count
 */
export function getUnreadCount() {
  return getStoredNotifications().filter(n => !n.read).length;
}

/**
 * Clear all notifications
 */
export function clearNotifications() {
  localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify([]));
}

// ========== PUSH NOTIFICATION SUPPORT ==========

/**
 * Request push notification permission
 */
export async function requestPushPermission() {
  if (!('Notification' in window)) {
    console.log('Browser does not support push notifications');
    return false;
  }

  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

/**
 * Show a browser push notification
 */
export function showPushNotification(title, body, options = {}) {
  if (Notification.permission !== 'granted') return;

  try {
    const notif = new Notification(`LYT: ${title}`, {
      body,
      icon: '/lyt_logo_dark.png',
      badge: '/lyt_logo_dark.png',
      tag: options.tag || `lyt-${Date.now()}`,
      requireInteraction: options.urgent || false,
      ...options,
    });

    notif.onclick = () => {
      window.focus();
      if (options.url) {
        window.location.hash = options.url;
      }
      notif.close();
    };

    return notif;
  } catch (err) {
    console.error('Push notification error:', err);
  }
}

// ========== EMAIL TEMPLATE ==========

function wrapEmailTemplate(title, body) {
  return `
    <div style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;color:#333;">
      <div style="background:linear-gradient(135deg,#0077B6,#00b4d8);padding:24px;text-align:center;border-radius:8px 8px 0 0;">
        <h1 style="color:#fff;margin:0;font-size:20px;">lyt Communications</h1>
        <p style="color:#fff;margin:4px 0 0;font-size:12px;opacity:0.8;">BUILDING DIGITAL FUTURES</p>
      </div>
      <div style="background:#fff;padding:24px;border:1px solid #eee;border-top:none;">
        <h2 style="margin-top:0;color:#0077B6;">${title}</h2>
        ${body}
      </div>
      <div style="background:#f8f9fa;padding:16px;text-align:center;border-radius:0 0 8px 8px;border:1px solid #eee;border-top:none;">
        <p style="margin:0;font-size:12px;color:#999;">This is an automated notification from LYT Communications</p>
        <p style="margin:4px 0 0;font-size:12px;color:#999;">Do not reply to this email</p>
      </div>
    </div>
  `;
}

// ========== DAILY COMPLIANCE DIGEST ==========

const ADMIN_EMAILS = ['matt@lytcomm.com', 'mason@lytcomm.com', 'donnie@lytcomm.com'];

/**
 * Generate and send a daily compliance digest email to all admins.
 * Scans all contractors and employees for expiring/expired items.
 * Call this from a scheduled trigger (e.g., Google Apps Script cron or Vercel cron).
 */
export async function sendDailyComplianceDigest() {
  try {
    const now = new Date();
    const alerts = { critical: [], urgent: [], warning: [] };

    // Fetch all contractor submissions
    const contractorResp = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsRead',
        sheetId: '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc',
        range: 'Contractor Submissions!A:Z',
      }),
    });

    // Fetch all employee submissions
    const employeeResp = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsRead',
        sheetId: '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc',
        range: 'Employee Submissions!A:Z',
      }),
    });

    const datasets = [];
    if (contractorResp.ok) {
      const d = await contractorResp.json();
      if (d.data && d.data.length > 1) datasets.push({ type: 'Contractor', data: d.data });
    }
    if (employeeResp.ok) {
      const d = await employeeResp.json();
      if (d.data && d.data.length > 1) datasets.push({ type: 'Employee', data: d.data });
    }

    const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const sevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const expirationFields = [
      { headerMatch: 'coi_expiration', label: 'COI' },
      { headerMatch: 'license_expiration', label: 'Business License' },
      { headerMatch: 'vehicle_insurance_exp', label: 'Vehicle Insurance' },
      { headerMatch: 'cert_expiration', label: 'Certification' },
      { headerMatch: 'osha_expiration', label: 'OSHA Cert' },
      { headerMatch: 'flagger_cert_exp', label: 'Flagger Cert' },
    ];

    for (const dataset of datasets) {
      const headers = dataset.data[0];
      const rows = dataset.data.slice(1);
      const nameIdx = headers.findIndex(h => h && (h.toString().toLowerCase().includes('company_name') || h.toString().toLowerCase().includes('full_name') || h.toString().toLowerCase().includes('name')));
      const emailIdx = headers.findIndex(h => h && h.toString().toLowerCase().includes('email'));

      for (const row of rows) {
        const name = (row[nameIdx] || row[emailIdx] || 'Unknown').toString();
        for (const field of expirationFields) {
          const idx = headers.findIndex(h => h && h.toString().toLowerCase().includes(field.headerMatch));
          if (idx === -1 || !row[idx]) continue;
          const expDate = new Date(row[idx]);
          if (isNaN(expDate.getTime())) continue;
          const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));

          if (expDate < now) {
            alerts.critical.push({ name, type: dataset.type, item: field.label, expired: expDate.toLocaleDateString(), daysLeft });
          } else if (expDate < sevenDays) {
            alerts.urgent.push({ name, type: dataset.type, item: field.label, expires: expDate.toLocaleDateString(), daysLeft });
          } else if (expDate < thirtyDays) {
            alerts.warning.push({ name, type: dataset.type, item: field.label, expires: expDate.toLocaleDateString(), daysLeft });
          }
        }
      }
    }

    const totalAlerts = alerts.critical.length + alerts.urgent.length + alerts.warning.length;
    if (totalAlerts === 0) return { sent: false, reason: 'No compliance alerts today' };

    // Build email body
    let body = `<p>Daily compliance scan found <strong>${totalAlerts} alert(s)</strong> as of ${now.toLocaleDateString()}:</p>`;

    if (alerts.critical.length > 0) {
      body += `<h3 style="color:#FF4444;margin-bottom:8px;">🚨 EXPIRED (${alerts.critical.length})</h3><table style="width:100%;border-collapse:collapse;margin-bottom:16px;">`;
      body += '<tr style="background:#fee;"><th style="text-align:left;padding:6px;border:1px solid #ddd;">Name</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Type</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Item</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Expired</th></tr>';
      for (const a of alerts.critical) {
        body += `<tr><td style="padding:6px;border:1px solid #ddd;">${a.name}</td><td style="padding:6px;border:1px solid #ddd;">${a.type}</td><td style="padding:6px;border:1px solid #ddd;">${a.item}</td><td style="padding:6px;border:1px solid #ddd;color:#FF4444;font-weight:bold;">${a.expired} (${Math.abs(a.daysLeft)}d ago)</td></tr>`;
      }
      body += '</table>';
    }

    if (alerts.urgent.length > 0) {
      body += `<h3 style="color:#FF9800;margin-bottom:8px;">⚠️ Expiring Within 7 Days (${alerts.urgent.length})</h3><table style="width:100%;border-collapse:collapse;margin-bottom:16px;">`;
      body += '<tr style="background:#fff8e1;"><th style="text-align:left;padding:6px;border:1px solid #ddd;">Name</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Type</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Item</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Expires</th></tr>';
      for (const a of alerts.urgent) {
        body += `<tr><td style="padding:6px;border:1px solid #ddd;">${a.name}</td><td style="padding:6px;border:1px solid #ddd;">${a.type}</td><td style="padding:6px;border:1px solid #ddd;">${a.item}</td><td style="padding:6px;border:1px solid #ddd;color:#FF9800;font-weight:bold;">${a.expires} (${a.daysLeft}d)</td></tr>`;
      }
      body += '</table>';
    }

    if (alerts.warning.length > 0) {
      body += `<h3 style="color:#2196F3;margin-bottom:8px;">📋 Expiring Within 30 Days (${alerts.warning.length})</h3><table style="width:100%;border-collapse:collapse;margin-bottom:16px;">`;
      body += '<tr style="background:#e3f2fd;"><th style="text-align:left;padding:6px;border:1px solid #ddd;">Name</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Type</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Item</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Expires</th></tr>';
      for (const a of alerts.warning) {
        body += `<tr><td style="padding:6px;border:1px solid #ddd;">${a.name}</td><td style="padding:6px;border:1px solid #ddd;">${a.type}</td><td style="padding:6px;border:1px solid #ddd;">${a.item}</td><td style="padding:6px;border:1px solid #ddd;">${a.expires} (${a.daysLeft}d)</td></tr>`;
      }
      body += '</table>';
    }

    body += '<p style="font-size:13px;color:#666;">Log in to <a href="https://lytcomm.com/#admin-dashboard">lytcomm.com</a> for full details.</p>';

    const subject = `LYT Daily Compliance Digest: ${alerts.critical.length} expired, ${alerts.urgent.length} urgent, ${alerts.warning.length} upcoming`;
    const html = wrapEmailTemplate('Daily Compliance Digest', body);

    // Send to all admins
    let sentCount = 0;
    for (const admin of ADMIN_EMAILS) {
      try {
        await sendEmailNotification(admin, subject, html);
        sentCount++;
      } catch (e) {
        console.error(`Failed to send digest to ${admin}:`, e);
      }
    }

    return { sent: true, sentTo: sentCount, totalAlerts, critical: alerts.critical.length, urgent: alerts.urgent.length, warning: alerts.warning.length };
  } catch (err) {
    console.error('sendDailyComplianceDigest error:', err);
    return { sent: false, error: err.message };
  }
}

// ========== COMPLIANCE HUB EMAIL ROUTING ==========
// All Aspen/Highline contacts are hardcoded here — invisible to subs

const ASPEN_DAMAGES_EMAIL = 'damages@aspenfiber.com';
const ASPEN_CONTACTS = {
  mattBlau: 'matt.blau@aspenfiber.com',
  ericaPawelek: 'erica.pawelek@aspenfiber.com',
  rodWoodfolk: 'rod.woodfolk@aspenfiber.com',
  nicholasWarehouse: 'nicholas@aspenfiber.com',
};
const LYT_MATT = 'matt@lytcomm.com';
const LYT_MASON = 'mason@lytcomm.com';

/**
 * Send Damage/Utility Strike Report email with PDF attachment
 * Routes to: Aspen Damages + CC Matt Blau, Erica, Rod, matt@lytcomm.com
 * Sub never sees these recipients
 */
export async function sendDamageReportEmail(formData, user, pdfDataUrl) {
  try {
    const userName = user?.company || user?.name || user?.email || 'Unknown';
    const subject = `Damage Report: ${formData.jbNumber || 'N/A'} — ${formData.location || 'Unknown location'}`;
    const body = `
      <h2 style="color:#dc3545;margin:0 0 16px">Damage / Utility Strike Report</h2>
      <p>Submitted by <strong>${userName}</strong> on ${new Date().toLocaleString()}</p>
      <table style="width:100%;border-collapse:collapse;margin:16px 0;">
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;width:180px;">JB Number</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.jbNumber || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Location</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.location || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">GPS</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.gpsLat && formData.gpsLng ? `${formData.gpsLat}, ${formData.gpsLng}` : 'Not captured'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Utility Type</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.utilityType || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Service/Main</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.serviceOrMain || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Marked/Unmarked</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.markedOrUnmarked || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Utility Owner</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.utilityOwner || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Homes Affected</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.homesAffected || '0'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Crew Lead</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.crewLead || 'N/A'}</td></tr>
      </table>
      <p><strong>Description:</strong> ${formData.description || 'N/A'}</p>
      <p><strong>Immediate Actions:</strong> ${formData.immediateActions || 'N/A'}</p>
      <p style="font-size:12px;color:#999;margin-top:24px;">Full report PDF attached. Auto-generated by LYT Communications portal.</p>
    `;

    // Send to Aspen Damages (primary), CC'd contacts, and Matt
    const recipients = [
      ASPEN_DAMAGES_EMAIL,
      ASPEN_CONTACTS.mattBlau,
      ASPEN_CONTACTS.ericaPawelek,
      ASPEN_CONTACTS.rodWoodfolk,
      LYT_MATT,
    ];
    for (const email of recipients) {
      await sendEmailNotification(email, subject, body);
    }

    addNotification({
      type: NOTIFICATION_TYPES.ISSUE_REPORTED,
      severity: SEVERITY.URGENT,
      title: `Damage Report: ${formData.jbNumber || 'N/A'}`,
      message: `${formData.utilityType || 'Utility'} strike at ${formData.location || 'unknown location'}`,
    });
  } catch (err) {
    console.error('[notificationService] sendDamageReportEmail failed:', err);
  }
}

/**
 * Send Pothole Verification email with PDF
 * Routes to: mason@lytcomm.com (field ops)
 */
export async function sendPotholeVerificationEmail(formData, user, pdfDataUrl) {
  try {
    const userName = user?.company || user?.name || user?.email || 'Unknown';
    const subject = `Pothole Verification: ${formData.address || 'Unknown'} — ${formData.project || ''}`;
    const body = `
      <h2 style="color:#fd7e14;margin:0 0 16px">Pothole Verification Report</h2>
      <p>Submitted by <strong>${userName}</strong> on ${new Date().toLocaleString()}</p>
      <table style="width:100%;border-collapse:collapse;margin:16px 0;">
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;width:180px;">Project</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.project || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Address</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.address || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">GPS</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.gpsLat && formData.gpsLng ? `${formData.gpsLat}, ${formData.gpsLng}` : 'Not captured'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Depth</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.depth || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Utility Found</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.utilityFound || 'N/A'}</td></tr>
        ${formData.utilityFound === 'Yes' ? `
          <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Utility Type</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.utilityType || 'N/A'}</td></tr>
          <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Utility Depth</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.utilityDepth || 'N/A'}</td></tr>
          <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Utility Direction</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.utilityDirection || 'N/A'}</td></tr>
        ` : ''}
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Crew Lead</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.crewLead || 'N/A'}</td></tr>
      </table>
      ${formData.notes ? `<p><strong>Notes:</strong> ${formData.notes}</p>` : ''}
      <p style="font-size:12px;color:#999;margin-top:24px;">Full report PDF attached. Auto-generated by LYT Communications portal.</p>
    `;

    await sendEmailNotification(LYT_MASON, subject, body);

    addNotification({
      type: NOTIFICATION_TYPES.POTHOLE_SUBMITTED,
      severity: SEVERITY.INFO,
      title: `Pothole: ${formData.address || 'Unknown'}`,
      message: `${formData.utilityFound === 'Yes' ? formData.utilityType + ' found' : 'No utility found'} — ${formData.project || ''}`,
    });
  } catch (err) {
    console.error('[notificationService] sendPotholeVerificationEmail failed:', err);
  }
}

/**
 * Send Material Request email with PDF
 * Routes to: matt@lytcomm.com
 */
export async function sendMaterialRequestEmail(formData, user, pdfDataUrl) {
  try {
    const userName = user?.company || user?.name || user?.email || 'Unknown';
    const urgencyLabel = formData.urgency === 'Emergency' ? ' [EMERGENCY]' : formData.urgency === 'Urgent' ? ' [URGENT]' : '';
    const subject = `Material Request${urgencyLabel}: ${formData.materialType || 'Materials'} — ${formData.project || ''}`;
    const urgencyColor = formData.urgency === 'Emergency' ? '#dc3545' : formData.urgency === 'Urgent' ? '#fd7e14' : '#4CAF50';
    const body = `
      <h2 style="color:#0d6efd;margin:0 0 16px">Material Request</h2>
      <p>Submitted by <strong>${userName}</strong> on ${new Date().toLocaleString()}</p>
      ${formData.urgency !== 'Standard' ? `<div style="background:${urgencyColor}15;border-left:4px solid ${urgencyColor};padding:12px 16px;margin:12px 0;border-radius:0 8px 8px 0;"><strong style="color:${urgencyColor};">${formData.urgency.toUpperCase()}</strong> — ${formData.urgency === 'Emergency' ? 'Needed ASAP' : 'Needed within 24 hours'}</div>` : ''}
      <table style="width:100%;border-collapse:collapse;margin:16px 0;">
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;width:180px;">Project</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.project || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Material Type</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.materialType || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Description</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.description || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Quantity</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.qty || '0'} ${formData.unit || ''}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Delivery Location</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.deliveryLocation || 'N/A'}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Needed By</td><td style="padding:8px 12px;border:1px solid #eee;">${formData.neededByDate || 'Standard (48 hrs)'}</td></tr>
      </table>
      <p style="font-size:12px;color:#999;margin-top:24px;">Auto-generated by LYT Communications portal.</p>
    `;

    await sendEmailNotification(LYT_MATT, subject, body);

    addNotification({
      type: NOTIFICATION_TYPES.STATUS_CHANGE,
      severity: formData.urgency === 'Emergency' ? SEVERITY.URGENT : SEVERITY.INFO,
      title: `Material Request: ${formData.materialType || 'Materials'}`,
      message: `${formData.qty || ''} ${formData.unit || ''} for ${formData.project || 'project'}`,
    });
  } catch (err) {
    console.error('[notificationService] sendMaterialRequestEmail failed:', err);
  }
}

// ========== MAP NOTE NOTIFICATIONS ==========

/**
 * Notify assignee when a map note is assigned to them
 */
export async function notifyNoteAssigned(email, noteText, assignedBy, project, crewType) {
  try {
    const subject = `Map Note Assigned: ${project}`;
    const crewLabel = crewType ? `<tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Crew Type</td><td style="padding:8px 12px;border:1px solid #eee;">${crewType}</td></tr>` : '';
    const body = `
      <h2 style="color:#FF9800;margin:0 0 16px">You've Been Assigned a Map Note</h2>
      <p><strong>${assignedBy}</strong> assigned you a note on project <strong>${project}</strong>.</p>
      <div style="background:#fff3e0;border-left:4px solid #FF9800;padding:12px 16px;margin:16px 0;border-radius:0 8px 8px 0;">
        <p style="margin:0;white-space:pre-wrap;">${noteText}</p>
      </div>
      <table style="width:100%;border-collapse:collapse;margin:16px 0;">
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Project</td><td style="padding:8px 12px;border:1px solid #eee;">${project}</td></tr>
        ${crewLabel}
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Assigned By</td><td style="padding:8px 12px;border:1px solid #eee;">${assignedBy}</td></tr>
      </table>
      <p style="margin:20px 0">
        <a href="https://lytcomm.com/#project-map"
           style="background:#FF9800; color:#fff; padding:12px 24px; border-radius:8px; text-decoration:none; font-weight:600; display:inline-block">
          View on Map
        </a>
      </p>
    `;
    await sendEmailNotification(email, subject, body);
    addNotification({
      type: NOTIFICATION_TYPES.ASSIGNMENT_NEW,
      severity: SEVERITY.INFO,
      title: `Note assigned on ${project}`,
      message: noteText.slice(0, 100),
    });
  } catch (err) {
    console.warn('[notificationService] notifyNoteAssigned failed:', err);
  }
}

/**
 * Notify admins when a field worker reports a note as complete
 */
export async function notifyNoteCompleteReported(noteText, completedBy, completionNotes) {
  try {
    const subject = `Map Note Completed: ${completedBy}`;
    const body = `
      <h2 style="color:#009688;margin:0 0 16px">Map Note Marked Complete</h2>
      <p><strong>${completedBy}</strong> reports this note as resolved:</p>
      <div style="background:#e0f2f1;border-left:4px solid #009688;padding:12px 16px;margin:16px 0;border-radius:0 8px 8px 0;">
        <p style="margin:0;white-space:pre-wrap;">${noteText}</p>
      </div>
      ${completionNotes ? `<p><strong>Completion notes:</strong> ${completionNotes}</p>` : ''}
      <p>Please verify in the <a href="https://lytcomm.com/#project-map">Project Map</a>.</p>
    `;
    for (const admin of ADMIN_EMAILS) {
      await sendEmailNotification(admin, subject, body);
    }
    addNotification({
      type: NOTIFICATION_TYPES.ISSUE_RESOLVED,
      severity: SEVERITY.INFO,
      title: 'Note completed',
      message: `${completedBy}: ${noteText.slice(0, 80)}`,
    });
  } catch (err) {
    console.warn('[notificationService] notifyNoteCompleteReported failed:', err);
  }
}

/**
 * Notify admins of a safety concern (urgent — email + push)
 */
export async function notifySafetyConcern(noteText, author, project, lat, lng) {
  try {
    const mapsLink = `https://www.google.com/maps?q=${lat},${lng}`;
    const subject = `SAFETY CONCERN: ${project}`;
    const body = `
      <h2 style="color:#F44336;margin:0 0 16px">Safety Concern Reported</h2>
      <div style="background:#ffebee;border-left:4px solid #F44336;padding:12px 16px;margin:16px 0;border-radius:0 8px 8px 0;">
        <p style="margin:0;font-weight:600;color:#c62828;">IMMEDIATE ATTENTION REQUIRED</p>
      </div>
      <p><strong>${author}</strong> flagged a safety concern on project <strong>${project}</strong>:</p>
      <div style="background:#fff3e0;border-left:4px solid #FF9800;padding:12px 16px;margin:16px 0;border-radius:0 8px 8px 0;">
        <p style="margin:0;white-space:pre-wrap;">${noteText}</p>
      </div>
      <table style="width:100%;border-collapse:collapse;margin:16px 0;">
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Project</td><td style="padding:8px 12px;border:1px solid #eee;">${project}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Reported By</td><td style="padding:8px 12px;border:1px solid #eee;">${author}</td></tr>
        <tr><td style="padding:8px 12px;border:1px solid #eee;font-weight:600;">Location</td><td style="padding:8px 12px;border:1px solid #eee;"><a href="${mapsLink}">View on Google Maps</a></td></tr>
      </table>
      <p style="margin:20px 0">
        <a href="https://lytcomm.com/#project-map"
           style="background:#F44336; color:#fff; padding:12px 24px; border-radius:8px; text-decoration:none; font-weight:600; display:inline-block">
          View on Project Map
        </a>
      </p>
    `;
    for (const admin of ADMIN_EMAILS) {
      await sendEmailNotification(admin, subject, body);
    }
    // Push notification for immediate visibility
    showPushNotification(
      `SAFETY: ${project}`,
      `${author}: ${noteText.slice(0, 100)}`,
      { urgent: true, tag: 'safety-concern', url: '#project-map' }
    );
    addNotification({
      type: NOTIFICATION_TYPES.ISSUE_REPORTED,
      severity: SEVERITY.CRITICAL,
      title: `SAFETY: ${project}`,
      message: `${author}: ${noteText.slice(0, 100)}`,
    });
  } catch (err) {
    console.warn('[notificationService] notifySafetyConcern failed:', err);
  }
}

// v1.3.0 — added map note notifications
